import React, {useMemo} from "react";
import {Alert, Button, Spinner, Table} from "react-bootstrap";
import {PiArrowLeft} from "react-icons/pi";
import {Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis} from "recharts";
import useStatisticsData from "./useStatisticsData.js";
import {formatEuro, formatEuroCompact, formatMonthPeriod, formatPercent, formatQuantity} from "./format.js";

/**
 * Detailansicht eines einzelnen Sortiments (Übernummer): großes
 * Verlaufsdiagramm plus die Varianten-Tabelle darunter. Die Varianten kommen
 * bereits vollständig (nicht serverseitig paginiert) mit der Detailantwort -
 * die Anzahl bleibt je Sortiment überschaubar (Farbvarianten einer
 * Übernummer), eine eigene DataGrid-Anbindung mit Server-Paging wäre hier
 * Overkill.
 */
export default function SortimentDetail({parentArticleNumber, filters, metric, granularity, color, onBack}) {
    const params = useMemo(
        () => ({...filters, parent_article_number: parentArticleNumber, granularity}),
        [filters, parentArticleNumber, granularity]
    );
    const {data, loading, error} = useStatisticsData("/statistics/sortiment-detail", params);

    const variants = useMemo(() => {
        const rows = data?.variants || [];
        return [...rows].sort((a, b) => (b.net_amount || 0) - (a.net_amount || 0));
    }, [data]);

    return (
        <div className="d-flex flex-column gap-3">
            <div>
                <Button variant="outline-secondary" size="sm" onClick={onBack} className="d-inline-flex align-items-center gap-1">
                    <PiArrowLeft aria-hidden="true" />
                    Zurück zur Übersicht
                </Button>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            {loading && !data ? (
                <div className="d-flex justify-content-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Wird geladen…</span>
                    </Spinner>
                </div>
            ) : (
                data && (
                    <>
                        <div>
                            <h2 className="h5 mb-0">{data.parent_name || data.parent_article_number}</h2>
                            <div className="small text-muted">
                                {data.parent_article_number} · {data.article_group || "ohne Warengruppe"}
                            </div>
                        </div>

                        <div className="card shadow-sm">
                            <div className="card-header fw-semibold">Umsatzverlauf</div>
                            <div className="card-body">
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart data={data.series || []} margin={{top: 8, right: 12, left: 0, bottom: 0}}>
                                        <defs>
                                            <linearGradient id="sortiment-detail-fill" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor={color} stopOpacity={0.35} />
                                                <stop offset="95%" stopColor={color} stopOpacity={0.03} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--bs-border-color, #dee2e6)" />
                                        <XAxis dataKey="period" tickFormatter={formatMonthPeriod} tick={{fontSize: 12}} axisLine={false} tickLine={false} />
                                        <YAxis
                                            tickFormatter={metric === "quantity" ? formatQuantity : formatEuroCompact}
                                            tick={{fontSize: 12}}
                                            axisLine={false}
                                            tickLine={false}
                                            width={70}
                                        />
                                        <Tooltip
                                            formatter={(value) => [metric === "quantity" ? `${formatQuantity(value)} Stk.` : formatEuro(value), metric === "quantity" ? "Menge" : "Nettoumsatz"]}
                                            labelFormatter={(label) => formatMonthPeriod(label)}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey={metric}
                                            stroke={color}
                                            strokeWidth={2}
                                            fill="url(#sortiment-detail-fill)"
                                            dot={{r: 3}}
                                            activeDot={{r: 5}}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="card shadow-sm">
                            <div className="card-header fw-semibold">Varianten</div>
                            <div className="card-body p-0">
                                {variants.length === 0 ? (
                                    <div className="text-muted small text-center py-4">Keine Varianten mit Absatz im gewählten Zeitraum.</div>
                                ) : (
                                    <div className="table-responsive">
                                        <Table hover size="sm" className="mb-0 align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Artikelnummer</th>
                                                    <th>Bezeichnung</th>
                                                    <th className="text-end">Menge</th>
                                                    <th className="text-end">Nettoumsatz</th>
                                                    <th className="text-end">Anteil</th>
                                                    <th>Erster Verkauf</th>
                                                    <th>Letzter Verkauf</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {variants.map((variant) => (
                                                    <tr key={variant.article_number}>
                                                        <td>{variant.article_number}</td>
                                                        <td>{variant.name || "–"}</td>
                                                        <td className="text-end">{formatQuantity(variant.quantity)} Stk.</td>
                                                        <td className="text-end">{formatEuro(variant.net_amount)}</td>
                                                        <td className="text-end">{formatPercent(variant.share_pct)}</td>
                                                        <td>{formatMonthPeriod(variant.first_sale_month)}</td>
                                                        <td>{formatMonthPeriod(variant.last_sale_month)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )
            )}
        </div>
    );
}
